// Transliteration map is duplicated on client and server (see
// `./transliterateForSms.ts` and `server/src/Utils/transliterate-for-sms.ts`).
// `shared/` cannot be used because it is not picked up by both build setups.
// Keep both copies in sync.
import { transliterateForSms } from "./transliterateForSms";
import type { ChannelKey } from "../types";

// SMS-segment estimator. Assumes GSM-7 plaintext encoding at 160
// chars/part, falling back to UCS-2 (70 chars/part) the moment the body
// contains any character outside the common GSM alphabet. The provider
// is the authoritative segmenter at send time; this util drives only
// the editor counter (DEV-8042).
//
// Diacritic transliteration: outbound SMS bodies are transliterated to
// ASCII before send (čšž → csz, ä → ae, …) to keep messages on GSM-7.
// The counter applies the same transformation so the displayed segment
// budget matches what providers actually receive — otherwise a
// Slovenian operator sees "70 chars / part" and panics, while the
// outbound message is comfortably under 160.

const GSM_BASIC =
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?" +
  "¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
const GSM_EXT = "\f^{}\\[~]|€";

/**
 * Per-channel single-message capacity (DEV-8042). SMS uses GSM-7's
 * 160-char single-segment budget; Viber and WhatsApp Business messages
 * are not segmented and accept up to 1000 characters per message.
 * Mirrors `MESSENGER_MAX_CHARACTERS` in `server/src/Utils/messenger-character-limit.ts`.
 */
export const CHANNEL_SINGLE_MESSAGE_CAPACITY: Record<ChannelKey, number> = {
  email: Number.POSITIVE_INFINITY,
  sms: 160,
  viber: 1000,
  whatsapp: 1000,
};

function isGsm7(text: string): boolean {
  for (const ch of text) {
    if (!GSM_BASIC.includes(ch) && !GSM_EXT.includes(ch)) return false;
  }
  return true;
}

/**
 * Number of SMS parts the message will consume. The text is
 * transliterated first so diacritics don't push the encoding into
 * UCS-2, since the send pipeline transliterates too.
 */
export function countSmsParts(text: string): number {
  const normalized = transliterateForSms(text);
  if (!normalized) return 0;
  if (isGsm7(normalized)) {
    const len = normalized.length;
    if (len <= 160) return 1;
    return Math.ceil(len / 153);
  }
  const len = [...normalized].length; // UCS-2 counts code points
  if (len <= 70) return 1;
  return Math.ceil(len / 67);
}

/**
 * Returns the per-segment character capacity given the post-
 * transliteration content. Single-segment messages on GSM-7 get 160;
 * multi-segment messages drop to 153 (UDH header). UCS-2 messages get
 * 70 / 67 respectively. The editor counter pairs this with
 * `transliteratedLength` so users see "X / 160 znakov".
 */
export function smsCapacityFor(text: string): number {
  const normalized = transliterateForSms(text);
  if (isGsm7(normalized)) {
    return normalized.length <= 160 ? 160 : 153;
  }
  return [...normalized].length <= 70 ? 70 : 67;
}

/**
 * Length the SMS pipeline will count against the segment budget — the
 * transliterated character count, not the raw editor input length.
 */
export function transliteratedLength(text: string): number {
  if (!text) return 0;
  return [...transliterateForSms(text)].length;
}

export type ChannelMessageBudget = {
  /** Characters the channel will count, after transliteration for SMS. */
  charCount: number;
  /** Per-segment capacity (SMS) or single-message limit (Viber/WhatsApp). */
  capacity: number;
  /** Number of SMS parts; always 1 for non-SMS channels. */
  parts: number;
  /** Whether the body exceeds the single-message budget for the channel. */
  isOverLimit: boolean;
};

/**
 * Computes the per-channel character / segment budget the editor
 * counter needs to render. Accepts an already-expanded body (e.g.
 * `expandForCharUsage` output) so the counter reflects worst-case
 * tag expansions.
 */
export function messageBudgetFor(
  channel: ChannelKey,
  body: string,
): ChannelMessageBudget {
  if (channel === "email") {
    return {
      charCount: body.length,
      capacity: Number.POSITIVE_INFINITY,
      parts: 0,
      isOverLimit: false,
    };
  }
  if (channel === "sms") {
    const charCount = transliteratedLength(body);
    const parts = countSmsParts(body);
    return {
      charCount,
      capacity: smsCapacityFor(body),
      parts,
      // Counter goes red when content spills past a single segment so
      // operators can quickly see the per-message-cost cliff.
      isOverLimit: parts > 1,
    };
  }
  // Viber / WhatsApp: the platforms accept rich text up to ~1000 chars
  // per message. We don't transliterate (those channels carry the
  // original body), and there's no segmentation — just a single hard
  // cap with red overflow indication.
  const cap = CHANNEL_SINGLE_MESSAGE_CAPACITY[channel];
  return {
    charCount: body.length,
    capacity: cap,
    parts: 1,
    isOverLimit: body.length > cap,
  };
}
